/**
 * WorkflowJobsPanel
 *
 * Displays all user's workflow jobs (pending, processing, completed).
 * Shows progress for active jobs and allows viewing completed reports.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { jobsService, WorkflowJob } from '../services/jobsService';
import { useNavigate } from 'react-router-dom';
import { BETA_TOOL_LABELS } from '../constants/betaToolLabels';

interface WorkflowJobsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const formatWorkflowType = (workflowType: string): string => {
  const typeMap: Record<string, string> = {
    ...BETA_TOOL_LABELS,
    'loss_run_analysis': 'Loss Run Analysis',
    'loss_run_analyzer': 'Loss Run Analyzer',
    'loss_run_analyzer_v4': 'Loss Run Analyzer (legacy NEW)',
    'loss_run_analyzer_v4_3': 'Loss Run Analysis',
    'renewal_analysis': 'Renewal Analysis',
    'year_over_year': 'Year Over Year',
    'yoy_renewal': 'YOY Renewal',
    'personal_lines_yoy_renewal': 'Personal Lines YOY',
    'commercial_lines_exec_renewal': 'Commercial Lines Exec',
    'coverage_analysis': 'Coverage Analysis',
    'insurance_requirements': 'Insurance Requirements',
    'document_comparison': 'Document Comparison',
    'research': 'Research',
    'policy_comparison': 'Policy Comparison',
    'financial_analysis': 'Financial Analysis',
    'client_renewal_presentation': 'Client Renewal Presentation',
    'certificate_of_effort': 'Certificate of Effort',
    'universal_analysis': 'Universal Analysis',
    'rate_analysis': 'Rate Analysis',
    'portfolio_analyzer': 'Portfolio Analyzer',
  };

  return typeMap[workflowType] || workflowType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getStatusColor = (status: WorkflowJob['status']): string => {
  switch (status) {
    case 'pending':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
    case 'processing':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
    case 'completed':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case 'failed':
    case 'dead_letter':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    case 'cancelled':
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

const getDisplayError = (raw: string | null): string => {
  if (!raw) return 'An error occurred. Please try again.';
  const m = raw.toLowerCase();
  if (
    m.includes('too large') ||
    m.includes('too big') ||
    m.includes('exceeds') ||
    m.includes('size limit') ||
    m.includes('payload') ||
    m.includes('413') ||
    m.includes('request entity')
  ) {
    return 'Documents are too large to process. Try splitting them into smaller files.';
  }
  if (m.includes('timeout') || m.includes('timed out')) {
    return 'Processing timed out. Please try again.';
  }
  return 'An error occurred. Please try again.';
};

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
};

const WorkflowJobsPanel: React.FC<WorkflowJobsPanelProps> = ({ isOpen, onClose }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<WorkflowJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<string | null>(null);

  const fetchJobs = useCallback(async () => {
    if (!token) return;

    try {
      setError(null);
      const response = await jobsService.getUserJobs(token, true);
      if (response.success) {
        setJobs(response.jobs);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      fetchJobs();
      // Poll for updates while panel is open
      const interval = setInterval(fetchJobs, 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen, fetchJobs]);

  const handleRetry = async (jobId: string) => {
    if (!token) return;
    setRetrying(jobId);
    try {
      await jobsService.retryJob(token, jobId);
      await fetchJobs();
    } catch (err: any) {
      setError(err.message || 'Failed to retry job');
    } finally {
      setRetrying(null);
    }
  };

  const handleCancel = async (jobId: string) => {
    if (!token) return;
    setCancelling(jobId);
    try {
      await jobsService.cancelJob(token, jobId);
      await fetchJobs();
    } catch (err: any) {
      setError(err.message || 'Failed to cancel job');
    } finally {
      setCancelling(null);
    }
  };

  const handleViewReport = async (jobId: string, savedReportId: string) => {
    // Mark notification as sent so it doesn't keep showing in bell
    if (token) {
      try {
        await jobsService.markNotificationSent(token, jobId);
      } catch (err) {
        console.error('Failed to mark notification sent:', err);
      }
    }
    navigate(`/workflow-report?reportId=${savedReportId}`);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Workflow Jobs
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded dark:bg-red-900/30 dark:border-red-600 dark:text-red-300">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No workflow jobs found. Start a workflow to see it here.
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h3 className="font-medium text-gray-900 dark:text-white">
                          {formatWorkflowType(job.workflowType)}
                        </h3>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getStatusColor(job.status)}`}>
                          {job.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {job.customer?.name || 'Unknown Customer'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Started {formatTimeAgo(job.createdAt)}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      {job.status === 'completed' && job.savedReportId && (
                        <button
                          onClick={() => handleViewReport(job.id, job.savedReportId!)}
                          className="px-3 py-1 text-sm bg-primary text-white rounded hover:bg-blue-600 transition-colors"
                        >
                          View Report
                        </button>
                      )}
                      {(job.status === 'pending' || job.status === 'processing') && (
                        <button
                          onClick={() => handleCancel(job.id)}
                          disabled={cancelling === job.id}
                          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                        >
                          {cancelling === job.id ? 'Cancelling...' : 'Cancel'}
                        </button>
                      )}
                      {(job.status === 'failed' || job.status === 'dead_letter') && (
                        <button
                          onClick={() => handleRetry(job.id)}
                          disabled={retrying === job.id || job.attempts >= job.maxAttempts}
                          className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                        >
                          {retrying === job.id ? 'Retrying...' : 'Retry'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Progress bar for processing jobs */}
                  {(job.status === 'pending' || job.status === 'processing') && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400 mb-1">
                        <span>{job.progressMessage || 'Processing...'}</span>
                        <span>{job.progress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                        <div
                          className="bg-primary rounded-full h-2 transition-all duration-300"
                          style={{ width: `${job.progress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Error message for failed jobs */}
                  {(job.status === 'failed' || job.status === 'dead_letter') && (
                    <div className="mt-3 p-2 bg-red-50 dark:bg-red-900/20 rounded text-xs text-red-700 dark:text-red-300 break-words">
                      <strong>Error:</strong> {getDisplayError(job.lastError)}
                    </div>
                  )}

                  {/* Attempt count */}
                  {job.attempts > 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                      Attempt {job.attempts} of {job.maxAttempts}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={fetchJobs}
            className="px-4 py-2 text-sm text-primary hover:text-blue-700 dark:hover:text-blue-400"
          >
            Refresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default WorkflowJobsPanel;
